import { CurlValidationError } from '../errors/CurlValidationError';

const CURL_ERROR_MESSAGE = {
  INVALID_STRUCTURE:
    'Invalid JSON structure. Check for missing commas, braces, or other syntax errors.',
  EMPTY_CURL: 'cURL command cannot be empty.',
  NO_URL: 'No valid URL found in cURL command.',
  CURL_COMMAND_MISSING: "Command should start with 'curl'.",
  UNBALANCED_SINGLE_QUOTE: 'Unbalanced single quotes in cURL command.',
  UNBALANCED_DOUBLE_QUOTE: 'Unbalanced double quotes in cURL command.',
  INVALID_DATA_PARAM:
    'Data parameters (-d, --data, --data-raw, etc.) should be wrapped in quotes or reference a file with @.',
  INVALID_HEADER: 'Headers (-H or --header) should be wrapped in quotes.',
};
const VALID_CURL_MESSAGE = '✓ cURL command syntax looks valid!';
const REGEX_PATTERN = {
  URL: /https?:\/\/\S+/g,
  CREDENTIAL: /\${[^}]+\.SECRET}/g,
  NEWLINE_MULTILINE: /\\\s*\n/g,
  PLACEHOLDER: /\${[^}]+}/g,
  HEADER: /-H\s+(['"])(.*?)\1|\-\-header\s+(['"])(.*?)\3/g,
  DOUBLE_QUOTES: /(?<!\\)"/g,
  SINGLE_QUOTES: /(?<!\\)'/g,
  DATA_PARAM: [
    /-d\s+['"]/,
    /--data\s+['"]/,
    /--data-raw\s+['"]/,
    /--data-urlencode\s+['"]/,
    /--data-binary\s+['"]/,
  ],
};

/**
 * Validates curl command syntax
 * @param {string} curlCommand - The curl command to validate
 * @throws {CurlValidationError} - When validation fails
 * @returns {string} - Success message when validation passes
 */
export const validateCurlSyntax = curlCommand => {
  let newlineProcessedCommand = curlCommand.replace(REGEX_PATTERN.NEWLINE_MULTILINE, ' ');
  const placeholders = newlineProcessedCommand.match(REGEX_PATTERN.PLACEHOLDER) || [];
  let processedCommand = replacePlaceholdersWithDummyValues(placeholders, newlineProcessedCommand);

  checkIfCurlCommand(processedCommand);
  checkIfQuotesAreBalanced(processedCommand);
  checkIfCurlHeadersAreValid(processedCommand);
  checkIfDataParamIsValid(processedCommand);
  checkIfApiUrlIsPresent(processedCommand, placeholders);
  return VALID_CURL_MESSAGE;
};

const checkIfCurlCommand = curlCommand => {
  if (curlCommand?.trim() === '') {
    throw new CurlValidationError(CURL_ERROR_MESSAGE.EMPTY_CURL);
  }

  if (!curlCommand.trim().toLowerCase().startsWith('curl ')) {
    throw new CurlValidationError(CURL_ERROR_MESSAGE.CURL_COMMAND_MISSING);
  }
};

const checkIfQuotesAreBalanced = processedCommand => {
  const noOfDoubleQuotes = (processedCommand.match(REGEX_PATTERN.DOUBLE_QUOTES) || []).length;
  const noOfSingleQuotes = (processedCommand.match(REGEX_PATTERN.SINGLE_QUOTES) || []).length;

  if (noOfDoubleQuotes % 2 !== 0) {
    throw new CurlValidationError(CURL_ERROR_MESSAGE.UNBALANCED_DOUBLE_QUOTE);
  }

  if (noOfSingleQuotes % 2 !== 0) {
    throw new CurlValidationError(CURL_ERROR_MESSAGE.UNBALANCED_SINGLE_QUOTE);
  }
};

const checkIfCurlHeadersAreValid = processedCommand => {
  if (containsHeader(processedCommand)) {
    const headerMatches = processedCommand.match(REGEX_PATTERN.HEADER);

    if (!headerMatches) {
      throw new CurlValidationError(CURL_ERROR_MESSAGE.INVALID_HEADER);
    }
  }
};

const checkIfDataParamIsValid = processedCommand => {
  if (containsDataParam(processedCommand)) {
    const quotedSections = extractQuotedContent(processedCommand);

    const hasValidDataParam = REGEX_PATTERN.DATA_PARAM.some(pattern =>
      pattern.test(processedCommand)
    );

    if (
      !hasValidDataParam &&
      !processedCommand.includes('-d @') &&
      !processedCommand.includes('--data @')
    ) {
      throw new CurlValidationError(CURL_ERROR_MESSAGE.INVALID_DATA_PARAM);
    }

    if (
      hasValidDataParam &&
      (processedCommand.includes('application/json') ||
        processedCommand.includes('Content-Type: json'))
    ) {
      const jsonContent = quotedSections.find(
        section => section.trim().startsWith('{') && section.trim().endsWith('}')
      );

      if (jsonContent) {
        validateJsonContent(jsonContent);
      }
    }
  }
};

const checkIfApiUrlIsPresent = (processedCommand, placeholders) => {
  const credentials = processedCommand.match(REGEX_PATTERN.CREDENTIAL) || [];
  const hasCredentialPlaceholder = credentials.length > 0;
  const urlPresent =
    processedCommand.match(REGEX_PATTERN.URL) ||
    placeholders.some(
      p => p.toLowerCase().includes('url') || p.toLowerCase().includes('endpoint')
    ) ||
    (hasCredentialPlaceholder && processedCommand.includes('Authorization'));

  if (!urlPresent) {
    throw new CurlValidationError(CURL_ERROR_MESSAGE.NO_URL);
  }
};

const containsHeader = processedCommand => {
  return processedCommand.includes('-H') || processedCommand.includes('--header');
};

const containsDataParam = processedCommand => {
  return (
    processedCommand.includes('-d') ||
    processedCommand.includes('--data') ||
    processedCommand.includes('--data-raw') ||
    processedCommand.includes('--data-urlencode') ||
    processedCommand.includes('--data-binary')
  );
};

const replacePlaceholdersWithDummyValues = (placeholders, processedCommand) => {
  let placeholderReplacedCommand = processedCommand;
  placeholders.forEach((placeholder, index) => {
    placeholderReplacedCommand = processedCommand.replace(
      placeholder,
      `PLACEHOLDER_${index}_TOKEN`
    );
  });
  return placeholderReplacedCommand;
};

const extractQuotedContent = text => {
  const quotedSections = [];
  let inSingleQuote = false;
  let inDoubleQuote = false;
  let currentSection = '';
  let lastChar = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === "'" && lastChar !== '\\' && !inDoubleQuote) {
      inSingleQuote = !inSingleQuote;
      if (!inSingleQuote) {
        quotedSections.push(currentSection);
        currentSection = '';
      }
    } else if (char === '"' && lastChar !== '\\' && !inSingleQuote) {
      inDoubleQuote = !inDoubleQuote;
      if (!inDoubleQuote) {
        quotedSections.push(currentSection);
        currentSection = '';
      }
    } else if (inSingleQuote || inDoubleQuote) {
      currentSection += char;
    }

    lastChar = char;
  }

  return quotedSections;
};

const validateJsonContent = jsonContent => {
  const jsonPlaceholders = extractPlaceholders(jsonContent);
  validatePlaceholderQuoting(jsonContent, jsonPlaceholders);

  validateJsonStructure(jsonContent);
};

const extractPlaceholders = jsonContent => {
  const jsonPlaceholders = [];
  let placeholderMatch;

  while ((placeholderMatch = REGEX_PATTERN.PLACEHOLDER.exec(jsonContent)) !== null) {
    jsonPlaceholders.push({
      placeholder: placeholderMatch[0],
      index: placeholderMatch.index,
    });
  }

  return jsonPlaceholders;
};

const validatePlaceholderQuoting = (jsonContent, placeholders) => {
  for (const placeholder of placeholders) {
    if (
      !isPlaceholderInQuotes(jsonContent, placeholder.index) &&
      isPlaceholderValuePosition(jsonContent, placeholder.index)
    ) {
      throw new CurlValidationError(
        `Placeholder ${placeholder.placeholder} in JSON should be wrapped in double quotes.`
      );
    }
  }
};

const isPlaceholderInQuotes = (jsonContent, placeholderIndex) => {
  let inQuote = false;
  let escaping = false;

  for (let i = 0; i < placeholderIndex; i++) {
    const char = jsonContent[i];
    if (char === '\\' && !escaping) {
      escaping = true;
      continue;
    }
    if (char === '"' && !escaping) {
      inQuote = !inQuote;
    }
    escaping = false;
  }

  return inQuote;
};

const isPlaceholderValuePosition = (jsonContent, placeholderIndex) => {
  const beforePlaceholder = jsonContent.substring(0, placeholderIndex).trim();
  const lastColon = beforePlaceholder.lastIndexOf(':');

  return (
    lastColon > beforePlaceholder.lastIndexOf('{') && lastColon > beforePlaceholder.lastIndexOf('[')
  );
};

const validateJsonStructure = jsonContent => {
  const validatableJson = jsonContent.replace(REGEX_PATTERN.PLACEHOLDER, '"placeholder_value"');
  try {
    JSON.parse(validatableJson);
  } catch (e) {
    throw new CurlValidationError(CURL_ERROR_MESSAGE.INVALID_STRUCTURE);
  }
};
